/**
 * Real Tracking API Integration
 * Actual API calls for AfterShip and Shopify
 */

window.RealTrackingAPI = {
  config: {
    aftershipKey: 'asat_60417bd33f9940b3b6eb2f9528732ac5',
    shopifyDomain: 'hallyu4u.myshopify.com',
    shopifyToken: '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'
  },

  /**
   * Get customer orders from Shopify
   */
  getCustomerOrders: async function() {
    try {
      // Get customer ID from global config
      const customerId = window.HALLYU4U_CONFIG?.customer?.id;

      if (!customerId) {
        console.log('No customer logged in');
        return [];
      }

      // Shopify Storefront API endpoint
      const endpoint = `https://${this.config.shopifyDomain}/api/2025-10/customers/${customerId}/orders.json`;

      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'X-Shopify-Access-Token': this.config.shopifyToken,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        return data.orders || [];
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    }

    // Return empty array as fallback
    return [];
  },

  /**
   * Get form submissions from Powerful Form Builder
   */
  getFormSubmissions: async function() {
    try {
      const customerId = window.HALLYU4U_CONFIG?.customer?.id;

      if (!customerId) {
        return [];
      }

      // Use backend API instead of non-existent Form Builder API
      const backendUrl = 'https://junie-unprotesting-jaelynn.ngrok-free.dev';
      const customerEmail = window.HALLYU4U_CONFIG?.customer?.email;

      const response = await fetch(`${backendUrl}/api/submissions/list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true'
        },
        body: JSON.stringify({
          customerId: customerId,
          customerEmail: customerEmail
        })
      });

      if (response.ok) {
        const data = await response.json();
        return data.submissions || [];
      }
    } catch (error) {
      console.log('Using localStorage for form submissions');
    }

    // Fallback to localStorage
    const stored = localStorage.getItem('form_submissions');
    if (stored) {
      try {
        const submissions = JSON.parse(stored);
        return submissions.filter(s => s.customerId === customerId);
      } catch (e) {
        console.error('Error parsing stored submissions:', e);
      }
    }

    return [];
  },

  /**
   * Track package with AfterShip
   */
  trackPackage: async function(trackingNumber) {
    try {
      // First detect carrier
      const detectResponse = await fetch('https://api.aftership.com/v4/couriers/detect', {
        method: 'POST',
        headers: {
          'aftership-api-key': this.config.aftershipKey,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tracking: {
            tracking_number: trackingNumber
          }
        })
      });

      if (!detectResponse.ok) {
        throw new Error('Failed to detect carrier');
      }

      const detectData = await detectResponse.json();
      const carrier = detectData.data?.couriers?.[0]?.slug;

      if (!carrier) {
        throw new Error('Carrier not detected');
      }

      // Create tracking if not exists
      const createResponse = await fetch('https://api.aftership.com/v4/trackings', {
        method: 'POST',
        headers: {
          'aftership-api-key': this.config.aftershipKey,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tracking: {
            tracking_number: trackingNumber,
            slug: carrier
          }
        })
      });

      // Get tracking info
      const trackingResponse = await fetch(`https://api.aftership.com/v4/trackings/${carrier}/${trackingNumber}`, {
        method: 'GET',
        headers: {
          'aftership-api-key': this.config.aftershipKey,
          'Content-Type': 'application/json'
        }
      });

      if (trackingResponse.ok) {
        const data = await trackingResponse.json();
        return data.data?.tracking;
      }
    } catch (error) {
      console.error('Error tracking package:', error);
    }

    return null;
  },

  /**
   * Get all trackings for customer
   */
  getCustomerTrackings: async function() {
    try {
      const customerEmail = window.HALLYU4U_CONFIG?.customer?.email;

      if (!customerEmail) {
        return [];
      }

      const response = await fetch('https://api.aftership.com/v4/trackings', {
        method: 'GET',
        headers: {
          'aftership-api-key': this.config.aftershipKey,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Filter by customer email if available
        const trackings = data.data?.trackings || [];
        return trackings.filter(t =>
          t.emails?.includes(customerEmail) ||
          t.customer_name === customerEmail
        );
      }
    } catch (error) {
      console.error('Error fetching trackings:', error);
    }

    return [];
  },

  /**
   * Submit form data
   */
  submitFormData: function(formType, formData) {
    const customerId = window.HALLYU4U_CONFIG?.customer?.id;

    if (!customerId) {
      console.error('No customer logged in');
      return false;
    }

    // Create submission object
    const submission = {
      id: `${formType}-${Date.now()}`,
      type: formType,
      customerId: customerId,
      customerEmail: window.HALLYU4U_CONFIG.customer.email,
      data: formData,
      status: 'pending',
      date: new Date().toISOString()
    };

    // Save to localStorage
    try {
      const stored = localStorage.getItem('form_submissions') || '[]';
      const submissions = JSON.parse(stored);
      submissions.push(submission);
      localStorage.setItem('form_submissions', JSON.stringify(submissions));

      // Trigger event for dashboard update
      window.dispatchEvent(new CustomEvent('form-submission', {
        detail: submission
      }));

      return true;
    } catch (error) {
      console.error('Error saving submission:', error);
      return false;
    }
  },

  /**
   * Initialize real-time tracking
   */
  initRealTimeTracking: function() {
    // DISABLED: Auto-polling causes excessive API calls
    // Update only every 5 minutes instead of 30 seconds
    setInterval(() => {
      this.updateTrackingData();
    }, 300000); // Changed from 30000 (30s) to 300000 (5min)
  },

  /**
   * Update tracking data
   */
  updateTrackingData: async function() {
    const trackings = await this.getCustomerTrackings();

    // Emit update event
    window.dispatchEvent(new CustomEvent('tracking-update', {
      detail: { trackings }
    }));
  }
};

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    RealTrackingAPI.initRealTimeTracking();
  });
} else {
  RealTrackingAPI.initRealTimeTracking();
}